
import { supabase } from '@/integrations/supabase/client';

export interface PlayerTeamConnection {
  id: string;
  player_id: string;
  team_id: number;
  status: 'current' | 'previous' | 'applied_to';
  confirmed: boolean;
  confirmed_by?: string;
  created_at: string;
  updated_at: string;
}

export interface PlayerCoachConnection {
  id: string;
  player_id: string;
  coach_id: string;
  status: 'current' | 'previous' | 'fan_of';
  confirmed: boolean;
  confirmed_by?: string;
  created_at: string;
  updated_at: string;
}

export const playerConnectionsService = {
  // Team connections
  async createTeamConnection(
    playerId: string,
    teamId: number,
    status: 'current' | 'previous' | 'applied_to'
  ): Promise<PlayerTeamConnection> {
    console.log('Creating team connection:', { playerId, teamId, status });
    
    const { data, error } = await supabase
      .from('player_team_connections')
      .insert({
        player_id: playerId,
        team_id: teamId,
        status,
        confirmed: status === 'previous' // Previous connections are auto-confirmed
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating team connection:', error);
      console.error('Full error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw new Error(`Failed to create team connection: ${error.message}`);
    }
    
    console.log('Team connection created successfully:', data);
    return data as PlayerTeamConnection;
  },

  async getPlayerTeamConnections(playerId: string): Promise<PlayerTeamConnection[]> {
    const { data, error } = await supabase
      .from('player_team_connections')
      .select('*')
      .eq('player_id', playerId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as PlayerTeamConnection[];
  },

  async confirmTeamConnection(connectionId: string, confirmerId: string): Promise<void> {
    const { error } = await supabase
      .from('player_team_connections')
      .update({
        confirmed: true,
        confirmed_by: confirmerId,
        updated_at: new Date().toISOString()
      })
      .eq('id', connectionId);

    if (error) throw error;
  },

  // Coach connections
  async createCoachConnection(
    playerId: string,
    coachId: string,
    status: 'current' | 'previous' | 'fan_of'
  ): Promise<PlayerCoachConnection> {
    console.log('Creating coach connection:', { playerId, coachId, status });
    
    const { data, error } = await supabase
      .from('player_coach_connections')
      .insert({
        player_id: playerId,
        coach_id: coachId,
        status,
        confirmed: status === 'previous' || status === 'fan_of' // Previous and fan connections are auto-confirmed
      })
      .select()
      .single();

    if (error) {
      console.error('Error creating coach connection:', error);
      console.error('Full error details:', {
        message: error.message,
        details: error.details,
        hint: error.hint,
        code: error.code
      });
      throw new Error(`Failed to create coach connection: ${error.message}`);
    }
    
    console.log('Coach connection created successfully:', data);
    return data as PlayerCoachConnection;
  },

  async getPlayerCoachConnections(playerId: string): Promise<PlayerCoachConnection[]> {
    const { data, error } = await supabase
      .from('player_coach_connections')
      .select('*')
      .eq('player_id', playerId)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as PlayerCoachConnection[];
  },

  async confirmCoachConnection(connectionId: string, confirmerId: string): Promise<void> {
    const { error } = await supabase
      .from('player_coach_connections')
      .update({
        confirmed: true,
        confirmed_by: confirmerId,
        updated_at: new Date().toISOString()
      })
      .eq('id', connectionId);

    if (error) throw error;
  },

  // Get connections for coaches to confirm
  async getPendingTeamConnections(teamId: number): Promise<PlayerTeamConnection[]> {
    const { data, error } = await supabase
      .from('player_team_connections')
      .select('*')
      .eq('team_id', teamId)
      .eq('confirmed', false)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as PlayerTeamConnection[];
  },

  async getPendingCoachConnections(coachId: string): Promise<PlayerCoachConnection[]> {
    const { data, error } = await supabase
      .from('player_coach_connections')
      .select('*')
      .eq('coach_id', coachId)
      .eq('confirmed', false)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return (data || []) as PlayerCoachConnection[];
  }
};
